import { Icon as PhosphorIconType } from '@phosphor-icons/react';
import {
  AiGenerationStatus,
  Message,
  ResearchDataReferenceCode,
  ResearchDataReferenceImage,
  ResearchDataReferenceLLM,
  ResearchDataReferenceVideo,
  ResearchDataReferenceWebPage,
  SearchImageReference,
  SearchVideoReference,
  SearchWebPageReference,
} from './api';
import {
  NINJA_AGENT_AS_EXTERNAL_MODEL,
  SUPER_AGENT_AS_EXTERNAL_MODEL,
} from 'src/constants/llmResources';
import { IconName } from 'src/components/Icon';
import type { CotReasoning } from 'src/types/models/CotReasoning';

export type MediaReferences =
  | ResearchDataReferenceCode
  | ResearchDataReferenceImage
  | ResearchDataReferenceLLM
  | ResearchDataReferenceVideo
  | ResearchDataReferenceWebPage;

export enum MessageSkill {
  CODER = 'coder',
  RESEARCHER = 'researcher',
  DEEP_RESEARCHER = 'researcher-deep',
  SCHEDULER = 'scheduler',
  WRITER = 'writer',
  IMAGE_GENERATION = 'image',
  VIDEO_GENERATION = 'video',
  FILE = 'file',
}

export const MessageMediaSkills = [
  MessageSkill.IMAGE_GENERATION,
  MessageSkill.VIDEO_GENERATION,
];

type MessageMediaSkill = (typeof MessageMediaSkills)[number];

export const isMediaSkill = (skill: MessageSkill): skill is MessageMediaSkill =>
  MessageMediaSkills.includes(skill);

export enum RecommendationSkill {
  CODER = 'coder',
  RESEARCHER = 'researcher',
  SCHEDULER = 'scheduler',
  WRITER = 'writer',
  COPILOT = 'copilot',
  ASSISTANT = 'assistant',
}

export interface CreateMessageSharedLink {
  userId: string;
  messageId: string;
}

export enum ModalGalleryType {
  IMAGES_VIDEOS = 'IMAGES_VIDEOS',
  REFERENCES = 'REFERENCES',
  GENERATED_IMAGES = 'GENERATED_IMAGES',
  EXTERNAL_MODELS = 'EXTERNAL_MODELS',
  TEXT_FILE = 'TEXT_FILE',
}

export interface ExternalModelReference {
  url: string;
  title: string;
  icon_name: IconName | null;
  content: string;
  modelKey: string;
  originalQuery?: string;
}

export interface ExternalModelTabContent {
  tabTitle: string;
  iconName?: IconName;
  content: string;
  tabName: string;
  status?: AiGenerationStatus;
  reasoning?: CotReasoning;
}

export interface MessageTabData extends ExternalModelTabContent {
  references?: SearchWebPageReference[];
  IconComponent?: PhosphorIconType;
}

export interface TextFileItem {
  url: string;
  title: string;
  content: string;
}

export interface ImageGenerationItem {
  url: string;
  title: string;
  icon: IconName | null;
  source?: string;
  isDisabled?: boolean;
  withWatermark?: boolean;
}

export type ModalGalleryItemType =
  | SearchVideoReference
  | SearchImageReference
  | SearchWebPageReference
  | ExternalModelReference
  | ImageGenerationItem
  | TextFileItem;

export type ModalGalleryList = ModalGalleryItemType[];

export type Citation = SearchWebPageReference | ExternalModelReference;

export interface ExtendedMessage extends Message {
  isBlinking?: boolean;
}

export const MessageTabs = {
  NINJA_AGENT: NINJA_AGENT_AS_EXTERNAL_MODEL,
  SUPER_AGENT: SUPER_AGENT_AS_EXTERNAL_MODEL,
} as const;

export type TabPersistRegistryState = {
  isRead: boolean | null;
};

export type TabPersistRegistry = {
  [tabName: string]: TabPersistRegistryState;
};

export type MessageTabPersistRegistry = {
  [messageId: string]: TabPersistRegistry;
};

export type FeedbackOperationType = 'models' | 'message-level';

export type MediaType = 'image' | 'video';

export type CustomMessageParams = {
  overrideModelName?: string | null;
  shouldIgnoreThinkMode?: boolean;
  compareSearchParamName?: string;
  comparedConvId?: string | null;
};
